import {
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  Card,
  Table,
  Select,
  Modal,
  message,
  Spin,
  Descriptions,
  Checkbox,
  Dropdown,
  Tooltip,
} from "antd";
import {
  DownloadOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  ClearOutlined,
  ColumnHeightOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import ViewOneEmployeeRecord from "../../components/hr/ViewOneEmployeeRecord";
import dayjs from "dayjs";
import * as XLSX from "xlsx-js-style";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ALL_COLUMNS = [
  { key: "emp_code", title: "Employee Code", dataIndex: "emp_code", defaultVisible: true },
  { key: "employee_name", title: "Employee Name", dataIndex: "employee_name", defaultVisible: true },
  { key: "date", title: "Date", dataIndex: "date", defaultVisible: true },
  { key: "total_time", title: "Total Time", dataIndex: "total_time", defaultVisible: true },
  { key: "site_location", title: "Work Location", dataIndex: "site_location", defaultVisible: true },
  { key: "check_in_work_site", title: "Check-In Site", dataIndex: "check_in_work_site", defaultVisible: true },
  { key: "check_in_time", title: "Check-In Time", dataIndex: "check_in_time", defaultVisible: true },
  { key: "check_in_location", title: "Check-In Location", dataIndex: "check_in_location", defaultVisible: false, width: 300 },
  { key: "check_in_photo_url", title: "Check-In Photo", dataIndex: "check_in_photo_url", defaultVisible: false },
  { key: "check_out_work_site", title: "Check-Out Site", dataIndex: "check_out_work_site", defaultVisible: true },
  { key: "check_out_time", title: "Check-Out Time", dataIndex: "check_out_time", defaultVisible: true },
  { key: "check_out_location", title: "Check-Out Location", dataIndex: "check_out_location", defaultVisible: false, width: 300 },
  { key: "check_out_photo_url", title: "Check-Out Photo", dataIndex: "check_out_photo_url", defaultVisible: false },
  { key: "company_name", title: "Company Name", dataIndex: "company_name", defaultVisible: true },
  { key: "department", title: "Department", dataIndex: "department", defaultVisible: true },
  { key: "designation", title: "Designation", dataIndex: "designation", defaultVisible: false },
];

const defaultFilters = {
  employeeCode: "", employeeName: "", organisation: "", department: "",
  designation: "", fromDate: null, toDate: null,
};

export default function ViewEmployeeAttendance() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { authData, backUrl } = useContext(AuthContext);

  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showLead, setShowLead] = useState(false);
  const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  const [organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [draftFilters, setDraftFilters] = useState({ ...defaultFilters });
  const [appliedFilters, setAppliedFilters] = useState({ ...defaultFilters });

  const [showSingle, setShowSingle] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // ── Column visibility ─────────────────────────────────────────────────────
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("viewEmpAttendance_visibleColumns");
    if (saved) { try { return JSON.parse(saved); } catch (_) { } }
    return ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);
  });
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("viewEmpAttendance_visibleColumns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const handleColumnToggle = (key, e) => {
    e.stopPropagation();
    setVisibleColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
  const handleSelectAll = (e) => { e.stopPropagation(); setVisibleColumns(ALL_COLUMNS.map((c) => c.key)); };
  const handleDeselectAll = (e) => { e.stopPropagation(); setVisibleColumns([]); };
  const handleResetDefault = (e) => { e.stopPropagation(); setVisibleColumns(ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)); };

  const columnSelectorMenu = {
    items: [
      {
        key: "actions",
        label: (
          <div style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0", marginBottom: 8 }}>
            <Button size="small" type="link" onClick={handleSelectAll} style={{ color: "#0e497b" }}>Select All</Button>
            <Button size="small" type="link" onClick={handleDeselectAll} style={{ color: "#0e497b" }}>Deselect All</Button>
            <Button size="small" type="link" onClick={handleResetDefault} style={{ color: "#0e497b" }}>Reset</Button>
            <Button
              size="small" icon={<CloseCircleOutlined />} type="primary"
              onClick={() => setColumnDropdownOpen(false)}
              style={{ float: "right", backgroundColor: "white", color: "#0e497b" }}
            />
          </div>
        ),
      },
      ...ALL_COLUMNS.map((col) => ({
        key: col.key,
        label: (
          <Checkbox
            checked={visibleColumns.includes(col.key)}
            onChange={(e) => handleColumnToggle(col.key, e)}
            onClick={(e) => e.stopPropagation()}
          >
            {col.title}
          </Checkbox>
        ),
      })),
    ],
  };

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchOrganisations = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, { headers: { Authorization: `Bearer ${authData?.access}` } });
      setOrganisations(res.data.results || res.data);
    } catch { message.error("Failed to fetch organisations"); }
  };
  const fetchDepartments = async (orgId) => {
    if (!orgId) { setDepartments([]); return; }
    try {
      const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, { headers: { Authorization: `Bearer ${authData?.access}` } });
      setDepartments(res.data.results || res.data);
    } catch { setDepartments([]); }
  };
  const fetchDesignations = async (deptId) => {
    if (!deptId) { setDesignations([]); return; }
    try {
      const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`, { headers: { Authorization: `Bearer ${authData?.access}` } });
      setDesignations(res.data.results || res.data);
    } catch { setDesignations([]); }
  };

  const buildParams = (filters, page, size) => {
    const params = { page, page_size: size };
    if (filters.employeeCode) params.emp_code = filters.employeeCode;
    if (filters.employeeName) params.emp_name = filters.employeeName;
    if (filters.organisation) params.organisation = filters.organisation;
    if (filters.department) params.department = filters.department;
    if (filters.designation) params.designation = filters.designation;
    if (filters.fromDate) params.from_date = dayjs(filters.fromDate).format("YYYY-MM-DD");
    if (filters.toDate) params.to_date = dayjs(filters.toDate).format("YYYY-MM-DD");
    return params;
  };

  const fetchData = async (filters, page, size) => {
    setLoading(true);
    try {
      const res = await axios.get(`${backUrl}/api/hr/attendance_records`, {
        headers: { Authorization: `Bearer ${authData.access}` },
        params: buildParams(filters, page, size),
      });
      const records = res.data.results || res.data.attendance_records || [];
      setAttendanceData(records);
      setTotalRecords(res.data.count || records.length);
    } catch { message.error("Failed to load attendance data"); setAttendanceData([]); setTotalRecords(0); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOrganisations(); fetchData(defaultFilters, 1, 100); }, [authData?.access, backUrl]);
  useEffect(() => { fetchData(appliedFilters, currentPage, pageSize); }, [currentPage, pageSize]);
  useEffect(() => { if (draftFilters.organisation) fetchDepartments(draftFilters.organisation); else setDepartments([]); }, [draftFilters.organisation]);
  useEffect(() => { if (draftFilters.department) fetchDesignations(draftFilters.department); else setDesignations([]); }, [draftFilters.department]);

  const handleFilterChange = (key, value) => {
    setDraftFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "organisation") { next.department = ""; next.designation = ""; }
      else if (key === "department") { next.designation = ""; }
      return next;
    });
  };

  const handleSearch = () => {
    setAppliedFilters({ ...draftFilters }); setCurrentPage(1); setSelectedRowKeys([]);
    fetchData(draftFilters, 1, pageSize);
  };
  const handleClear = () => {
    setDraftFilters({ ...defaultFilters }); setAppliedFilters({ ...defaultFilters });
    setCurrentPage(1); setSelectedRowKeys([]);
    setDepartments([]); setDesignations([]);
    fetchData(defaultFilters, 1, pageSize);
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current); setPageSize(pagination.pageSize); setSelectedRowKeys([]);
  };

  // ── Download ──────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    try {
      message.loading("Preparing export...", 0);
      if (selectedRowKeys.length > 0) {
        exportToExcel(attendanceData.filter((r) => selectedRowKeys.includes(r.id)));
        return;
      }
      const res = await axios.get(`${backUrl}/api/hr/attendance_records`, {
        headers: { Authorization: `Bearer ${authData.access}` },
        params: buildParams(appliedFilters, 1, 999999),
      });
      message.destroy();
      exportToExcel(res.data.results || res.data.attendance_records || []);
    } catch { message.destroy(); message.error("Failed to export data"); }
  };

  const exportToExcel = (records) => {
    if (!records.length) { message.warning("No records to export"); return; }
    const XLSXStyle = require("xlsx-js-style");
    const activeCols = ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key));

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, name: "Arial", sz: 11 },
      fill: { fgColor: { rgb: "0E497B" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: { top: { style: "thin", color: { rgb: "FFFFFF" } }, bottom: { style: "thin", color: { rgb: "FFFFFF" } }, left: { style: "thin", color: { rgb: "FFFFFF" } }, right: { style: "thin", color: { rgb: "FFFFFF" } } },
    };
    const defaultStyle = {
      font: { name: "Arial", sz: 10 },
      alignment: { vertical: "center", wrapText: true },
      border: { top: { style: "thin", color: { rgb: "DDDDDD" } }, bottom: { style: "thin", color: { rgb: "DDDDDD" } }, left: { style: "thin", color: { rgb: "DDDDDD" } }, right: { style: "thin", color: { rgb: "DDDDDD" } } },
    };
    const altRowStyle = { ...defaultStyle, fill: { fgColor: { rgb: "F5F8FC" } } };

    const wsData = [activeCols.map((c) => ({ v: c.title, t: "s", s: headerStyle }))];
    records.forEach((row, idx) => {
      const rowStyle = idx % 2 === 0 ? defaultStyle : altRowStyle;
      wsData.push(activeCols.map((col) => {
        let v = "-";
        if (col.key === "date") v = row.date ? dayjs(row.date).format("DD/MM/YYYY") : "-";
        else if (col.key === "company_name") v = row.company_name || row.company || "-";
        else if (col.key === "check_in_photo_url" || col.key === "check_out_photo_url") v = row[col.dataIndex] ? "Photo Available" : "-";
        else v = row[col.dataIndex] || "-";
        return { v, t: "s", s: rowStyle };
      }));
    });

    const colLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const ws = XLSXStyle.utils.aoa_to_sheet(wsData.map((r) => r.map((c) => c.v)));
    wsData.forEach((row, rIdx) => {
      row.forEach((cell, cIdx) => {
        const ref = `${colLetters[cIdx]}${rIdx + 1}`;
        if (!ws[ref]) ws[ref] = {};
        ws[ref].s = cell.s;
      });
    });
    ws["!cols"] = activeCols.map(() => ({ wch: 18 }));
    ws["!rows"] = [{ hpt: 36 }, ...records.map(() => ({ hpt: 22 }))];
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, "Attendance");
    XLSXStyle.writeFile(wb, "Employee_Attendance.xlsx");
    message.success(`Exported ${records.length} records successfully`);
  };

  // ── Build table columns ───────────────────────────────────────────────────
  const getTableColumns = () => {
    const defs = {
      emp_code: {
        title: "Employee Code", dataIndex: "emp_code", key: "emp_code",
        render: (text, record) => (
          <a onClick={() => { setSelectedEmployee(record); setShowSingle(true); }}>{text}</a>
        ),
      },
      employee_name: { title: "Employee Name", dataIndex: "employee_name", key: "employee_name" },
      date: { title: "Date", dataIndex: "date", key: "date", render: (d) => d ? dayjs(d).format("DD/MM/YYYY") : "-" },
      total_time: { title: "Total Time", dataIndex: "total_time", key: "total_time" },
      site_location: { title: "Work Location", dataIndex: "site_location", key: "site_location", render: (v) => <p>{v}</p> },
      check_in_work_site: {
        title: "Check-In Site", dataIndex: "check_in_work_site", key: "check_in_work_site",
        render: (site, record) => site === "On-Duty" ? (
          <>
            <p style={{ marginBottom: 4 }}>{site}</p>
            {record?.onduty_type_check_in && <p style={{ marginBottom: 4, fontSize: 12, color: "#666" }}>{record.onduty_type_check_in}</p>}
            {record?.od_mode && <p style={{ marginBottom: 4, fontSize: 12, color: "#666" }}>{record.od_mode}</p>}
            {record?.od_status && <p style={{ marginBottom: 4, fontSize: 12, color: "#666" }}>{record.od_status}</p>}
          </>
        ) : <p>{site}</p>,
      },
      check_in_time: { title: "Check-In Time", dataIndex: "check_in_time", key: "check_in_time" },
      check_in_location: {
        title: "Check-In Location", dataIndex: "check_in_location", key: "check_in_location", width: 300,
        render: (_, record) =>
          record.check_in_latitude && record.check_in_longitude ? (
            <a href={`https://www.google.com/maps/search/?api=1&query=${record.check_in_latitude},${record.check_in_longitude}`}
              target="_blank" rel="noopener noreferrer" style={{ color: "#1677ff" }}>
              {record.check_in_location || "View Location"}
            </a>
          ) : (record.check_in_location || "-"),
      },
      check_in_photo_url: {
        title: "Check-In Photo", dataIndex: "check_in_photo_url", key: "check_in_photo_url",
        render: (url) => url ? <img src={url} alt="Check-In" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 8 }} /> : <span>No Image</span>,
      },
      check_out_work_site: {
        title: "Check-Out Site", dataIndex: "check_out_work_site", key: "check_out_work_site",
        render: (site, record) => site === "On-Duty" ? (
          <>
            <p style={{ marginBottom: 4 }}>{site}</p>
            {record?.onduty_type_check_out && <p style={{ marginBottom: 4, fontSize: 12, color: "#666" }}>{record.onduty_type_check_out}</p>}
            {record?.od_mode_check_out && <p style={{ marginBottom: 4, fontSize: 12, color: "#666" }}>{record.od_mode_check_out}</p>}
            {record?.od_status_check_out && <p style={{ marginBottom: 4, fontSize: 12, color: "#666" }}>{record.od_status_check_out}</p>}
          </>
        ) : <p>{site}</p>,
      },
      check_out_time: { title: "Check-Out Time", dataIndex: "check_out_time", key: "check_out_time" },
      check_out_location: {
        title: "Check-Out Location", dataIndex: "check_out_location", key: "check_out_location", width: 300,
        render: (_, record) =>
          record.check_out_latitude && record.check_out_longitude ? (
            <a href={`https://www.google.com/maps/search/?api=1&query=${record.check_out_latitude},${record.check_out_longitude}`}
              target="_blank" rel="noopener noreferrer" style={{ color: "#1677ff" }}>
              {record.check_out_location || "View Location"}
            </a>
          ) : (record.check_out_location || "-"),
      },
      check_out_photo_url: {
        title: "Check-Out Photo", dataIndex: "check_out_photo_url", key: "check_out_photo_url",
        render: (url) => url ? <img src={url} alt="Check-Out" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 8 }} /> : <span>No Image</span>,
      },
      company_name: { title: "Company Name", dataIndex: "company_name", key: "company_name", render: (text, record) => text || record.company || "-" },
      department: { title: "Department", dataIndex: "department", key: "department" },
      designation: { title: "Designation", dataIndex: "designation", key: "designation" },
    };

    return visibleColumns.filter((k) => defs[k]).map((k) => defs[k]);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {showSingle && (
        <Modal open={showSingle} footer={null} onCancel={() => setShowSingle(false)} width={1300}>
          <ViewOneEmployeeRecord employee={selectedEmployee} />
        </Modal>
      )}

      {showLead && (
        <Modal open={showLead} footer={null}
          onCancel={() => { setShowLead(false); setSelectedLeadDetails(null); }} width={700}>
          {selectedLeadDetails?.length > 0 ? (
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              {selectedLeadDetails.map((lead, index) => (
                <Descriptions key={index} bordered column={1} size="small" style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Lead Number"><strong>{lead.lead_no || "-"}</strong></Descriptions.Item>
                  <Descriptions.Item label="Customer Name">{lead.customer_name || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Email">{lead.email || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Phone Number">{lead.number || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Project Name">{lead.project_name || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <span style={{
                      padding: "4px 12px", borderRadius: "4px",
                      backgroundColor: lead.status?.toLowerCase().includes("cancel") ? "#fff2e8" : "#f6ffed",
                      color: lead.status?.toLowerCase().includes("cancel") ? "#d46b08" : "#52c41a",
                      fontWeight: 500,
                    }}>
                      {lead.status || "-"}
                    </span>
                  </Descriptions.Item>
                </Descriptions>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>No lead details available</div>
          )}
        </Modal>
      )}

      <center>
        <h5><ClockCircleOutlined /> Check-In & Check-Out</h5>
      </center>

      {/* Filters Card */}
      <Card style={{ margin: "20px", padding: "20px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Employee Code</label>
            <Input placeholder="Enter Employee Code" value={draftFilters.employeeCode}
              onChange={(e) => handleFilterChange("employeeCode", e.target.value)}
              allowClear onPressEnter={handleSearch} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Employee Name</label>
            <Input placeholder="Enter Employee Name" value={draftFilters.employeeName}
              onChange={(e) => handleFilterChange("employeeName", e.target.value)}
              allowClear onPressEnter={handleSearch} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Company Name</label>
            <Select placeholder="Select Company" style={{ width: "100%" }} allowClear
              value={draftFilters.organisation || undefined}
              onChange={(value) => handleFilterChange("organisation", value ?? "")}>
              {organisations.map((org) => <Option key={org.id} value={org.id}>{org.name}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Department</label>
            <Select showSearch placeholder="Select Department" style={{ width: "100%" }} allowClear
              value={draftFilters.department || undefined}
              onChange={(value) => handleFilterChange("department", value ?? "")}
              filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
              disabled={!draftFilters.organisation}>
              {departments.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
            </Select>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Designation</label>
            <Select showSearch placeholder="Select Designation" style={{ width: "100%" }} allowClear
              value={draftFilters.designation || undefined}
              onChange={(value) => handleFilterChange("designation", value ?? "")}
              filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
              disabled={!draftFilters.department}>
              {designations.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <label>Date Range</label>
            <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY"
              value={draftFilters.fromDate && draftFilters.toDate
                ? [dayjs(draftFilters.fromDate), dayjs(draftFilters.toDate)] : null}
              onChange={(dates) => {
                if (dates?.length === 2) setDraftFilters((prev) => ({ ...prev, fromDate: dates[0], toDate: dates[1] }));
                else setDraftFilters((prev) => ({ ...prev, fromDate: null, toDate: null }));
              }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={10} style={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}
              style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", height: "32px", minWidth: "100px" }}>
              Search
            </Button>
            <Button icon={<ClearOutlined />} onClick={handleClear} disabled={loading} style={{ height: "32px" }}>
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Action Bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0 0 10px", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <Button icon={<DownloadOutlined />}
            style={{ height: "36px", width: "220px", backgroundColor: "#0e497b", color: "white" }}
            onClick={handleDownload} disabled={loading}>
            Download {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : "All"}
          </Button>

          <Dropdown
            menu={columnSelectorMenu}
            trigger={["click"]}
            placement="bottomRight"
            open={columnDropdownOpen}
            onOpenChange={setColumnDropdownOpen}
          >
            <Tooltip title="Customize Columns">
              <Button icon={<ColumnHeightOutlined />}>
                Custom Columns ({visibleColumns.length}/{ALL_COLUMNS.length})
              </Button>
            </Tooltip>
          </Dropdown>
        </div>

        {selectedRowKeys.length > 0 && (
          <Button onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
        )}
      </div>

      <Spin spinning={loading} tip="Loading attendance records...">
        <Table
          style={{ marginTop: "10px" }}
          columns={getTableColumns()}
          dataSource={attendanceData}
          bordered
          pagination={{
            current: currentPage, pageSize, total: totalRecords,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
            pageSizeOptions: ["10", "20", "50", "100", "200"],
          }}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          rowSelection={{ selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) }}
          rowKey="id"
          components={{
            header: {
              cell: (props) => (
                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                  {props.children}
                </th>
              ),
            },
          }}
        />
      </Spin>
    </>
  );
}